// apps/codeframe/src/motion/sample.ts
import type { MotionTimeline, MotionTrack, MotionChannel, MotionProp } from '../store/types';
import { toEasingFn } from './ease';
import { lerp, mixColor } from './mathAdapter';

export interface AppliedStyle {
  transform?: string;
  opacity?: number;
  color?: string;
  backgroundColor?: string;
  filter?: string;
}

const COLOR_PROPS: MotionProp[] = ['color', 'backgroundColor'];

export function sampleChannel(ch: MotionChannel, t: number): number | string {
  const ks = ch.keyframes;
  if (ks.length === 0) return COLOR_PROPS.includes(ch.prop) ? 'transparent' : 0;
  if (t <= ks[0].t) return ks[0].value;
  if (t >= ks[ks.length - 1].t) return ks[ks.length - 1].value;
  let lo = 0, hi = ks.length - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (ks[mid].t <= t) lo = mid; else hi = mid;
  }
  const a = ks[lo], b = ks[hi];
  const span = b.t - a.t || 1;
  const p = toEasingFn(a.ease)((t - a.t) / span);
  if (COLOR_PROPS.includes(ch.prop)) return mixColor(String(a.value), String(b.value))(p);
  return lerp(Number(a.value), Number(b.value), p);
}

interface Accum { x: number; y: number; rotate: number; scaleX: number; scaleY: number; opacity?: number; color?: string; backgroundColor?: string; blur?: number; }

function defaults(): Accum { return { x: 0, y: 0, rotate: 0, scaleX: 1, scaleY: 1 }; }

export function sampleTrack(track: MotionTrack, t: number): AppliedStyle {
  const acc = defaults();
  for (const layer of track.layers) {
    const add = layer.blend === 'add';
    for (const ch of layer.channels) {
      const v = sampleChannel(ch, t);
      switch (ch.prop) {
        case 'x': acc.x = add ? acc.x + Number(v) : Number(v); break;
        case 'y': acc.y = add ? acc.y + Number(v) : Number(v); break;
        case 'rotate': acc.rotate = add ? acc.rotate + Number(v) : Number(v); break;
        case 'scale': { const n = Number(v); if (add) { acc.scaleX += n; acc.scaleY += n; } else { acc.scaleX = n; acc.scaleY = n; } break; }
        case 'scaleX': acc.scaleX = add ? acc.scaleX + Number(v) : Number(v); break;
        case 'scaleY': acc.scaleY = add ? acc.scaleY + Number(v) : Number(v); break;
        case 'opacity': acc.opacity = add ? (acc.opacity ?? 0) + Number(v) : Number(v); break;
        case 'blur': acc.blur = add ? (acc.blur ?? 0) + Number(v) : Number(v); break;
        case 'color': if (!add) acc.color = String(v); break;
        case 'backgroundColor': if (!add) acc.backgroundColor = String(v); break;
      }
    }
  }
  const out: AppliedStyle = {
    transform: `translate(${acc.x}px, ${acc.y}px) rotate(${acc.rotate}deg) scale(${acc.scaleX}, ${acc.scaleY})`,
  };
  if (acc.opacity !== undefined) out.opacity = Math.max(0, Math.min(1, acc.opacity));
  if (acc.color) out.color = acc.color;
  if (acc.backgroundColor) out.backgroundColor = acc.backgroundColor;
  if (acc.blur !== undefined) out.filter = `blur(${Math.max(0, acc.blur)}px)`;
  return out;
}

export function sampleFrame(tl: MotionTimeline, t: number): Map<string, AppliedStyle> {
  const out = new Map<string, AppliedStyle>();
  for (const track of tl.tracks) out.set(track.target, sampleTrack(track, t));
  return out;
}
